/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { useQuery } from '@apollo/client';

import Imgix from 'js/components/Imgix';

import { Divider, MenuList } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type {
  DegreeForProgramSwitcher as Degree,
  ThirdPartyOrganizationForProgramSwitcher as ThirdPartyOrganization,
} from 'bundles/composable-page-header/components/program-switcher/types';
import OrganizationCard, {
  ORG_SWITCHER_LOGO_WIDTH,
} from 'bundles/composable-page-header/components/user-experience-switcher/OrganizationCard';
import { useIsOrgOptedIntoOrgScopedHome } from 'bundles/composable-page-header/hooks/useIsOrgOptedIntoOrgScopedHome';
import { sortDegreesByLaunchedDate } from 'bundles/composable-page-header/utils/sortByLaunchedAt';
import EnterpriseLogo from 'bundles/page-header/components/EnterpriseLogo';
import { fillWithWhiteBackground } from 'bundles/page/common/imgixParams';
import { useUpdateUserExperience } from 'bundles/unified-common/contexts/UserExperienceContext';
import { clearProgramSwitcherSelection } from 'bundles/unified-common/hooks/useSyncProgramSwitcherSelection';
import { USER_EXPERIENCE_SWITCH_PARAM } from 'bundles/unified-common/utils/userExperienceRedirectUtils';
import ResolveProgramsByOrganizationQuery from 'bundles/unified-home-common/queries/ResolveProgramsByOrganizationQuery.graphql';
import type {
  ResolveProgramsByOrganizationQuery as ResolveProgramsByOrganizationQueryData,
  ResolveProgramsByOrganizationQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/ResolveProgramsByOrganizationQuery';

import CourseraLogo from 'images/page-header/coursera-logo-c-mark.png';

import _t from 'i18n!nls/composable-page-header';

export type Props = {
  currentOrganization?: ThirdPartyOrganization | null;
  currentDegree?: Degree | null;
  degrees?: Degree[] | null;
  organizations?: ThirdPartyOrganization[] | null;
};

type OnHomepageClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  homepageType: 'degree' | 'third_party_organization' | 'consumer',
  productId?: string
) => void;

const styles = {
  linkItem: css`
    &:focus,
    &:hover {
      text-decoration: none;
    }
  `,
  userExperienceSwitcherLink: (isCurrent: boolean) => css`
    margin-bottom: var(--cds-spacing-100);
    background-color: ${isCurrent ? 'var(--cds-color-grey-25)' : 'transparent'};

    :hover {
      text-decoration: none;
      background-color: var(--cds-color-grey-25);
    }
  `,
  organization: css`
    list-style: none;
    margin-top: var(--cds-spacing-100);
  `,
  divider: css`
    margin: var(--cds-spacing-200) 0;
  `,
};

const OrganizationListItem = ({
  organization,
  isCurrent,
  onHomepageClick,
}: {
  organization: ThirdPartyOrganization;
  isCurrent: boolean;
  onHomepageClick: OnHomepageClick;
}) => {
  const { name: orgName, slug: orgSlug, id: orgId } = organization;
  const isOrgScopedHomeEnabled = useIsOrgOptedIntoOrgScopedHome(orgId);

  const { data } = useQuery<ResolveProgramsByOrganizationQueryData, ResolveProgramsByOrganizationQueryVariables>(
    ResolveProgramsByOrganizationQuery,
    {
      variables: { orgSlug },
      skip: isOrgScopedHomeEnabled,
      context: { clientName: 'gatewayGql' },
    }
  );

  const firstProgramSlug = data?.EnterpriseProgram?.resolveProgramsByOrganization?.elements?.[0]?.slug;
  const orgHomeHref =
    isOrgScopedHomeEnabled || !firstProgramSlug ? `/organizations/${orgSlug}` : `/programs/${firstProgramSlug}`;

  return (
    <MenuList.ItemLink
      role="menuitem"
      key={orgId}
      css={[styles.linkItem, styles.userExperienceSwitcherLink(isCurrent)]}
      href={orgHomeHref}
      onClick={(e) => onHomepageClick(e, 'third_party_organization', orgId)}
    >
      <OrganizationCard
        name={_t("#{orgName}'s Internal Learning", { orgName })}
        description={_t('Managed by #{orgName}', { orgName })}
        isCurrent={isCurrent}
        logo={
          <EnterpriseLogo
            preferRectangle={false}
            thirdPartyOrganization={organization}
            logoWidth={ORG_SWITCHER_LOGO_WIDTH}
          />
        }
      />
    </MenuList.ItemLink>
  );
};

const OrganizationList = ({ organizations, degrees, currentDegree, currentOrganization }: Props) => {
  const sortedDegrees = useMemo(() => sortDegreesByLaunchedDate(degrees ?? []), [degrees]);
  const { update: updateUserExperience } = useUpdateUserExperience();

  const track = useTracker();

  const onHomepageClick = useCallback<OnHomepageClick>(
    async (
      e: React.MouseEvent<HTMLAnchorElement>,
      homepageType: 'degree' | 'third_party_organization' | 'consumer',
      productId?: string
    ) => {
      // Prevent immediate navigation so the mutation can persist before the page reloads.
      // Without this, the redirect hook on the target page reads stale context and bounces back.
      e.preventDefault();
      const targetUrl = new URL(e.currentTarget.href);
      targetUrl.searchParams.set(USER_EXPERIENCE_SWITCH_PARAM, '1');

      const previousHomepage = currentDegree?.id || currentOrganization?.id || '';
      let previousHomepageType: 'degree' | 'third_party_organization' | 'consumer' = 'consumer';
      if (currentDegree?.id) {
        previousHomepageType = 'degree';
      } else if (currentOrganization?.id) {
        previousHomepageType = 'third_party_organization';
      }

      track('switch_homepage', {
        previousHomepage,
        currentHomepage: productId || '',
        currentHomepageType: homepageType,
        previousHomepageType,
      });

      try {
        if (homepageType === 'consumer') {
          await Promise.all([updateUserExperience({ selection: 'consumer' }), clearProgramSwitcherSelection()]);
        } else if (homepageType === 'degree') {
          await updateUserExperience({ selection: 'degree', degreeProgramId: productId });
        } else if (homepageType === 'third_party_organization') {
          await updateUserExperience({ selection: 'enterprise', organizationId: productId });
        }
      } finally {
        window.location.assign(targetUrl.toString());
      }
    },
    // this function does not need to re-run when the dependencies ('currentDegree' and 'currentOrganization') change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // only show experience switcher for learners enrolled in multiple organizations or 1 or more degrees
  if ((organizations?.length ?? 0) < 2 && !degrees?.length) return null;

  return (
    <React.Fragment>
      <MenuList.ItemLink
        role="menuitem"
        key="personal-coursera-account"
        css={[styles.linkItem, styles.userExperienceSwitcherLink(!currentDegree && !currentOrganization)]}
        href="/"
        onClick={(e) => onHomepageClick(e, 'consumer')}
      >
        <OrganizationCard
          name={_t('Coursera Individual Learning')}
          description={_t('Personal Account')}
          isCurrent={!currentDegree && !currentOrganization}
          logo={<Imgix src={CourseraLogo} maxWidth={ORG_SWITCHER_LOGO_WIDTH} alt={_t('Coursera logo')} />}
        />
      </MenuList.ItemLink>
      {organizations?.map((organization) => (
        <OrganizationListItem
          key={organization.id}
          organization={organization}
          isCurrent={organization.id === currentOrganization?.id}
          onHomepageClick={onHomepageClick}
        />
      ))}
      {sortedDegrees?.map((degree) => {
        const { id: degreeId, name: degreeName, slug: degreeSlug, partners } = degree;

        const isCurrent = degreeId === currentDegree?.id;
        const degreeHref = `/degrees/${degreeSlug}/home`;
        const partnerSquareLogo = partners?.[0]?.squareLogo;

        return (
          <MenuList.ItemLink
            role="menuitem"
            key={degreeId}
            href={degreeHref}
            css={[styles.linkItem, styles.userExperienceSwitcherLink(isCurrent)]}
            onClick={(e) => onHomepageClick(e, 'degree', degreeId)}
          >
            <OrganizationCard
              name={degreeName}
              description={_t('Degree')}
              isCurrent={isCurrent}
              logo={
                partnerSquareLogo ? (
                  <Imgix
                    src={partnerSquareLogo}
                    maxWidth={ORG_SWITCHER_LOGO_WIDTH}
                    maxHeight={ORG_SWITCHER_LOGO_WIDTH}
                    alt={_t('Logo for #{degreeName}', { degreeName })}
                    imgParams={fillWithWhiteBackground}
                  />
                ) : undefined
              }
            />
          </MenuList.ItemLink>
        );
      })}
      <Divider css={styles.divider} />
    </React.Fragment>
  );
};

export default OrganizationList;
